import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { ReactNode } from 'react';
import { CloseIon } from 'src/components/icons/CloseIcon';
import { twMerge } from 'tailwind-merge';

interface DialogWrapperProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId: string;
  footer?: ReactNode;
  panelClassName?: string;
  disableClickOutside?: boolean;
}

export function DialogWrapper({
  title,
  onClose,
  children,
  testId,
  footer,
  panelClassName,
  disableClickOutside,
}: DialogWrapperProps) {
  return (
    <Dialog
      open
      onClose={disableClickOutside ? () => {} : onClose}
      className="relative z-50"
      data-testid={testId}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all sm:my-8 sm:p-6',
              'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
              'w-full max-w-md',
              panelClassName,
            ])}
            data-testid={`${testId}-panel`}
          >
            <DialogTitle
              as="h3"
              className="pb-4 text-xl font-bold"
              data-testid={`${testId}-title`}
            >
              {title}
            </DialogTitle>
            <div className="absolute right-4 top-4">
              <button
                type="button"
                onClick={onClose}
                data-testid={`${testId}-close-button`}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <CloseIon className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-4 space-y-6">{children}</div>

            {footer && <div className="mt-6">{footer}</div>}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
